/**
 * Device linking functions for cross-device user identity.
 * Allows users to add new devices to their existing account.
 */

import { acequiaReady, acequiaStore } from '../acequia/acequia.js'
import { jose } from '../deps.js'
import { keepPreviousIdentity, forgetCachedIdentities } from './identities.js'

/**
 * Create a device link token on an existing logged-in device.
 * This token can be used on a new device to link it to the same user account.
 *
 * @param {string} domain - The domain this link is for
 * @param {number} expiresInMinutes - How long the token is valid (default: 5 minutes)
 * @returns {Promise<string>} Signed JWT device-link token
 */
export async function createDeviceLinkToken(domain, expiresInMinutes = 5, targetUserId) {
  const { userId, userKeys, userPublicJWK, deviceId } = await acequiaReady()

  if (!userId) {
    throw new Error('No user logged in on this device')
  }

  if (!userKeys) {
    throw new Error('No user keys found on this device')
  }

  // Calculate kid from user public key
  const kid = await jose.calculateJwkThumbprint(userPublicJWK)

  const payload = {
    type: 'device-link',
    userId: targetUserId || userId,
    domain,
    createdBy: {
      kid,
      deviceId,
      userId,
    },
    singleUse: true,
  }

  const token = await new jose.SignJWT(payload)
    .setIssuedAt()
    .setExpirationTime(`${expiresInMinutes}m`)
    .setProtectedHeader({ alg: 'PS256', kid })
    .sign(userKeys.privateKey)

  return token
}

/**
 * Decode a device link token to get info about it (without accepting).
 *
 * @param {string} token - The device link token
 * @returns {Object} Decoded token info
 */
export function decodeDeviceLinkToken(token) {
  try {
    const decoded = jose.decodeJwt(token)
    const header = jose.decodeProtectedHeader(token)

    return {
      valid: true,
      type: decoded.type,
      userId: decoded.userId,
      domain: decoded.domain,
      createdBy: decoded.createdBy,
      issuedAt: decoded.iat ? new Date(decoded.iat * 1000) : null,
      expiresAt: decoded.exp ? new Date(decoded.exp * 1000) : null,
      isExpired: decoded.exp ? decoded.exp * 1000 < Date.now() : false,
      kid: header.kid,
    }
  } catch (err) {
    return {
      valid: false,
      error: err.message,
    }
  }
}

/**
 * Accept a device link token on a new device.
 * This will:
 * 1. Generate new user keys for this device
 * 2. Send the token and new public key to the server
 * 3. Store the userId and keys locally if successful
 *
 * @param {string} token - The device link token from an existing device
 * @param {string} authURL - The auth server URL (defaults to '/auth')
 * @returns {Promise<Object>} Result with user info
 */
export async function acceptDeviceLink(token, authURL = '/auth') {
  // Decode token to get userId and domain
  const decoded = decodeDeviceLinkToken(token)

  if (!decoded.valid) {
    throw new Error(`Invalid device link token: ${decoded.error}`)
  }

  if (decoded.type !== 'device-link') {
    throw new Error('Invalid token type')
  }

  if (decoded.isExpired) {
    throw new Error('Device link token has expired')
  }

  const { userId, domain } = decoded

  // Check if this device already has a different userId
  const existingUserId = await acequiaStore.getItem('userId')
  if (existingUserId && existingUserId !== userId) {
    console.log(
      `Replacing existing userId ${existingUserId} with ${userId} from device link`
    )
    // Keep it before clearing it: grants it minted are rooted in its key, and
    // stay this browser's to verify, renew and revoke (identities.js —
    // unified-token-system, decision 8, answer 1)
    const existingKeys = await acequiaStore.getItem('userKeys')
    if (existingKeys) {
      await keepPreviousIdentity({ userId: existingUserId, userKeys: existingKeys, linkedTo: userId })
        .catch((err) => console.warn('[deviceLink] could not keep the previous identity:', err.message))
    }
    // Clear existing user identity
    await acequiaStore.removeItem('userId')
    await acequiaStore.removeItem('userKeys')
  }

  // Get device ID (this should already exist)
  const { deviceId } = await acequiaReady()

  // Generate new user keys for this device
  const newUserKeys = await jose.generateKeyPair('PS256', {
    extractable: true,
  })

  const newPublicKey = await jose.exportJWK(newUserKeys.publicKey)
  const newKid = await jose.calculateJwkThumbprint(newPublicKey)

  // Send to server for validation and key registration
  const resp = await fetch(`${authURL}/device-link/accept`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token,
      newPublicKey,
      newDeviceId: deviceId,
      newKid,
      deviceName: (await acequiaStore.getItem('deviceName')) || undefined,
    }),
  })

  const result = await resp.json()

  if (!resp.ok || !result.success) {
    const err = new Error(result.message || 'Device link failed')
    if (result.code) err.code = result.code
    throw err
  }

  // Store userId, userKeys, and handle locally
  await acequiaStore.setItem('userId', userId)
  await acequiaStore.setItem('userKeys', newUserKeys)
  if (result.handle) {
    await acequiaStore.setItem('handle', result.handle)
  }

  console.log(`Device linked successfully: userId=${userId}, handle=${result.handle}, kid=${newKid}`)

  // The identity this browser had becomes the account's alias on the server
  // when the page next runs as the account: grant sync attests it first
  // (identities.attestAliases), so its grants sync and verify as the account's
  forgetCachedIdentities()

  return {
    success: true,
    userId,
    handle: result.handle,
    role: result.role,
    kid: newKid,
    deviceId,
    endorsedBy: result.endorsedBy,
    domain,
  }
}

/**
 * Get info about a device link token from the server.
 * This validates the token and returns user info.
 *
 * @param {string} token - The device link token
 * @param {string} authURL - The auth server URL (defaults to '/auth')
 * @returns {Promise<Object>} Token info from server
 */
export async function getDeviceLinkInfo(token, authURL = '/auth') {
  const resp = await fetch(`${authURL}/device-link/info`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  })

  return resp.json()
}

/**
 * Clear the current user identity from this device.
 * Use this for "logout" functionality.
 */
export async function clearUserIdentity() {
  await acequiaStore.removeItem('userId')
  await acequiaStore.removeItem('userKeys')
  console.log('User identity cleared from device')
}

/**
 * Check if this device has a user identity set.
 *
 * @returns {Promise<boolean>}
 */
export async function hasUserIdentity() {
  const userId = await acequiaStore.getItem('userId')
  const userKeys = await acequiaStore.getItem('userKeys')
  return !!(userId && userKeys)
}

/**
 * Get the current user's key ID (kid) on this device.
 *
 * @returns {Promise<string|null>} The kid or null if not logged in
 */
export async function getCurrentKid() {
  const { userKeys, userPublicJWK } = await acequiaReady()

  if (!userKeys || !userPublicJWK) {
    return null
  }

  return jose.calculateJwkThumbprint(userPublicJWK)
}
